import {
  camposPreview,
  type AtividadeCasa,
  type Autonomia,
  type Boletim,
  type Chegada,
  type Comportamento,
  type Desempenho,
  type Foco,
  type StatusRegistro,
} from '@shared/dto';

import {
  ATIVIDADE_CASA_LABEL,
  AUTONOMIA_LABEL,
  BOLETIM_LABEL,
  CHEGADA_LABEL,
  COMPORTAMENTO_LABEL,
  DESEMPENHO_LABEL,
  FOCO_LABEL,
} from '../components/common/registro-form/enum-labels';

/** Emoji por VALOR de cada campo; separado do emoji por TÓPICO (`*_TOPICO_EMOJI`) e por STATUS (`STATUS_EMOJI`) — os 3 nunca se misturam. */
export const CHEGADA_EMOJI: Record<Chegada, string> = {
  PRESENTE: '🟢',
  ATRASADO: '🟡',
  FALTOU: '🔴',
};

export const BOLETIM_EMOJI: Record<Boletim, string> = {
  PEGOU: '✓',
  NAO_PEGOU: '✕',
  PROBLEMA: '⚠',
};

export const ATIVIDADE_CASA_EMOJI: Record<AtividadeCasa, string> = {
  FEZ: '✅',
  FEZ_PARCIALMENTE: '🟡',
  NAO_FEZ: '❌',
  NAO_HAVIA: '➖',
};

/** Foco/Autonomia/Comportamento: enums diferentes, mesma escala visual de 4 níveis (pior → melhor), reaproveitada por posição. */
const NIVEL_EMOJI = ['😣', '😐', '🙂', '😄'] as const;

export const FOCO_EMOJI: Record<Foco, string> = {
  BAIXO: NIVEL_EMOJI[0],
  REGULAR: NIVEL_EMOJI[1],
  BOM: NIVEL_EMOJI[2],
  EXCELENTE: NIVEL_EMOJI[3],
};

export const AUTONOMIA_EMOJI: Record<Autonomia, string> = {
  BAIXA: NIVEL_EMOJI[0],
  REGULAR: NIVEL_EMOJI[1],
  BOA: NIVEL_EMOJI[2],
  EXCELENTE: NIVEL_EMOJI[3],
};

export const COMPORTAMENTO_EMOJI: Record<Comportamento, string> = {
  NECESSITOU_INTERVENCAO: NIVEL_EMOJI[0],
  OSCILOU: NIVEL_EMOJI[1],
  ADEQUADO: NIVEL_EMOJI[2],
  EXCELENTE: NIVEL_EMOJI[3],
};

/** Excelente ganha estrela (distinto de Bom) -- decisão explícita, os dois não usam o mesmo verde. */
export const DESEMPENHO_EMOJI: Record<Desempenho, string> = {
  PRECISOU_INTERVENCAO: '🔴',
  APRESENTOU_DIFICULDADE: '🟡',
  BOM: '🟢',
  EXCELENTE: '⭐',
};

/** Emoji por TÓPICO/CAMPO (fixo). `CHEGADA`/`BOLETIM`/`FOCO`/`AUTONOMIA` foram escolhidos por associação semântica. */
export const CHEGADA_TOPICO_EMOJI = '🕒';
export const BOLETIM_TOPICO_EMOJI = '📋';
export const ATIVIDADE_CASA_TOPICO_EMOJI = '📚';
export const FOCO_TOPICO_EMOJI = '🎯';
export const AUTONOMIA_TOPICO_EMOJI = '🧭';
export const COMPORTAMENTO_TOPICO_EMOJI = '🙂';
export const DESEMPENHO_TOPICO_EMOJI = '📊';

export const STATUS_EMOJI: Record<StatusRegistro, string> = {
  NAO_INICIADO: '⚪',
  PENDENTE: '⏰',
  CONCLUIDO: '✅',
};

export interface LinhaPreview {
  campoLabel: string;
  topicoEmoji: string;
  valorEmoji: string;
  label: string;
}

/** Linhas `[emoji-tópico][emoji-valor] Label` do preview; `camposPreview` decide QUAIS campos entram. */
export function linhasPreview(registro: {
  chegada: Chegada | null;
  boletim: Boletim | null;
  atividadeCasa: AtividadeCasa | null;
  foco: Foco | null;
  autonomia: Autonomia | null;
  comportamento: Comportamento | null;
  desempenho: Desempenho | null;
}): LinhaPreview[] {
  return camposPreview(registro).map((campo): LinhaPreview => {
    switch (campo) {
      case 'chegada':
        return {
          campoLabel: 'Chegada',
          topicoEmoji: CHEGADA_TOPICO_EMOJI,
          valorEmoji: CHEGADA_EMOJI[registro.chegada!],
          label: CHEGADA_LABEL[registro.chegada!],
        };
      case 'comportamento':
        return {
          campoLabel: 'Comportamento',
          topicoEmoji: COMPORTAMENTO_TOPICO_EMOJI,
          valorEmoji: COMPORTAMENTO_EMOJI[registro.comportamento!],
          label: COMPORTAMENTO_LABEL[registro.comportamento!],
        };
      case 'atividadeCasa':
        return {
          campoLabel: 'Atividade de casa',
          topicoEmoji: ATIVIDADE_CASA_TOPICO_EMOJI,
          valorEmoji: ATIVIDADE_CASA_EMOJI[registro.atividadeCasa!],
          label: ATIVIDADE_CASA_LABEL[registro.atividadeCasa!],
        };
      case 'desempenho':
        return {
          campoLabel: 'Desempenho',
          topicoEmoji: DESEMPENHO_TOPICO_EMOJI,
          valorEmoji: DESEMPENHO_EMOJI[registro.desempenho!],
          label: DESEMPENHO_LABEL[registro.desempenho!],
        };
    }
  });
}

/** Todos os campos preenchidos, sem o filtro de `camposPreview`: dado bruto pra aba "Dia" do Histórico. */
export function linhasCompletas(registro: {
  chegada: Chegada | null;
  boletim: Boletim | null;
  atividadeCasa: AtividadeCasa | null;
  foco: Foco | null;
  autonomia: Autonomia | null;
  comportamento: Comportamento | null;
  desempenho: Desempenho | null;
}): LinhaPreview[] {
  const linhas: LinhaPreview[] = [];
  if (registro.chegada) {
    linhas.push({
      campoLabel: 'Chegada',
      topicoEmoji: CHEGADA_TOPICO_EMOJI,
      valorEmoji: CHEGADA_EMOJI[registro.chegada],
      label: CHEGADA_LABEL[registro.chegada],
    });
  }
  if (registro.boletim) {
    linhas.push({
      campoLabel: 'Boletim',
      topicoEmoji: BOLETIM_TOPICO_EMOJI,
      valorEmoji: BOLETIM_EMOJI[registro.boletim],
      label: BOLETIM_LABEL[registro.boletim],
    });
  }
  if (registro.atividadeCasa) {
    linhas.push({
      campoLabel: 'Atividade de casa',
      topicoEmoji: ATIVIDADE_CASA_TOPICO_EMOJI,
      valorEmoji: ATIVIDADE_CASA_EMOJI[registro.atividadeCasa],
      label: ATIVIDADE_CASA_LABEL[registro.atividadeCasa],
    });
  }
  if (registro.foco) {
    linhas.push({
      campoLabel: 'Foco',
      topicoEmoji: FOCO_TOPICO_EMOJI,
      valorEmoji: FOCO_EMOJI[registro.foco],
      label: FOCO_LABEL[registro.foco],
    });
  }
  if (registro.autonomia) {
    linhas.push({
      campoLabel: 'Autonomia',
      topicoEmoji: AUTONOMIA_TOPICO_EMOJI,
      valorEmoji: AUTONOMIA_EMOJI[registro.autonomia],
      label: AUTONOMIA_LABEL[registro.autonomia],
    });
  }
  if (registro.comportamento) {
    linhas.push({
      campoLabel: 'Comportamento',
      topicoEmoji: COMPORTAMENTO_TOPICO_EMOJI,
      valorEmoji: COMPORTAMENTO_EMOJI[registro.comportamento],
      label: COMPORTAMENTO_LABEL[registro.comportamento],
    });
  }
  if (registro.desempenho) {
    linhas.push({
      campoLabel: 'Desempenho',
      topicoEmoji: DESEMPENHO_TOPICO_EMOJI,
      valorEmoji: DESEMPENHO_EMOJI[registro.desempenho],
      label: DESEMPENHO_LABEL[registro.desempenho],
    });
  }
  return linhas;
}
